(() => {
  'use strict';

  if (window.__productPiecesAsyncRegistered) return;
  window.__productPiecesAsyncRegistered = true;

  // Index wrappers are deliberately one shard per Shopify Liquid render.
  const CARD_BATCH_SIZE = 4;
  // Bound the number of speculative positions to protect Shopify.
  const FAST_PREVIEW_MAX_POSITIONS = 32;
  const FIRST_PAGE_PRIORITY_TIMEOUT = 4000;
  const INDEX_SECTIONS = {
    1: 'product-pieces-index-01',
    2: 'product-pieces-index-02',
    3: 'product-pieces-index-03',
    4: 'product-pieces-index-04',
    5: 'product-pieces-index-05',
    6: 'product-pieces-index-06',
    7: 'product-pieces-index-07',
    8: 'product-pieces-index-08',
    9: 'product-pieces-index-09',
    10: 'product-pieces-index-10',
    11: 'product-pieces-index-11',
    12: 'product-pieces-index-12',
    13: 'product-pieces-index-13'
  };

  const PIECES_DEBUG =
    new URLSearchParams(window.location.search).get('pieces_debug') === '1';

  let piecesRequestId = 0;

  const sleep = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds));

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const parseHtml = (html) =>
    new DOMParser().parseFromString(html, 'text/html');

  const buildUrl = (sectionId, pageParam, pageNumber) => {
    const url = new URL(window.location.href);

    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('page_')) url.searchParams.delete(key);
    }

    url.searchParams.set('section_id', sectionId);

    if (pageParam && pageNumber) {
      url.searchParams.set(pageParam, String(pageNumber));
    }

    return url;
  };

  const requestSection = async (
    sectionId, pageParam, pageNumber, maxAttempts = 3
  ) => {
    let lastError;
    const requestId = PIECES_DEBUG ? ++piecesRequestId : null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const startedAt = PIECES_DEBUG ? performance.now() : 0;
      let httpStatus = null;
      try {
        const response = await fetch(
          buildUrl(sectionId, pageParam, pageNumber),
          { credentials: 'same-origin', cache: 'no-store' }
        );
        httpStatus = response.status;

        const html = await response.text();
        const liquidError = html.match(/Liquid error[^<\n]*/g);

        if (!response.ok || liquidError?.length) {
          throw new Error(
            `${sectionId}: HTTP ${response.status}; ` +
            (liquidError?.join(' | ') || 'section unavailable')
          );
        }

        const doc = parseHtml(html);

        if (PIECES_DEBUG) {
          console.info('[pieces:request:ok]', {
            requestId,
            sectionId,
            page: pageNumber ?? 1,
            attempt: `${attempt}/${maxAttempts}`,
            durationMs: Math.round(performance.now() - startedAt),
            status: response.status
          });
        }

        return doc;
      } catch (error) {
        lastError = error;

        if (PIECES_DEBUG) {
          console.warn('[pieces:request:error]', {
            requestId,
            sectionId,
            page: pageNumber ?? 1,
            attempt: `${attempt}/${maxAttempts}`,
            durationMs: Math.round(performance.now() - startedAt),
            status: httpStatus,
            error: error.message,
            nextRetryMs: attempt < maxAttempts
              ? attempt * 500
              : null
          });
        }

        if (attempt < maxAttempts) await sleep(attempt * 500);
      }
    }

    throw lastError;
  };

  const initialize = (root) => {
    if (root.dataset.piecesInitialized === 'true') return;
    root.dataset.piecesInitialized = 'true';

    const productHandle = root.dataset.productHandle;
    const legacyMode = root.dataset.sourceMode === 'legacy';
    const fastFirstCards = root.dataset.fastFirstCards === 'true';
    const prefetchCardPagination =
      !legacyMode &&
      root.dataset.prefetchCardPagination === 'true';
    const useHybridIndex =
      !legacyMode && root.dataset.useHybridIndex === 'true';
    const prioritizeFirstPage =
      !legacyMode &&
      root.dataset.prioritizeFirstPage === 'true';
    const earlyIndexRendering =
      !legacyMode &&
      !prioritizeFirstPage &&
      root.dataset.earlyIndexRendering === 'true';
    const progressiveCardRendering =
      fastFirstCards || (
        !legacyMode && root.dataset.progressiveCardRendering === 'true'
      );
    const optimizedCardLoading =
      !legacyMode &&
      root.dataset.optimizedCardLoading === 'true';

    const shards = legacyMode
      ? [0]
      : (root.dataset.shards || '')
        .split(',')
        .map(Number)
        .filter((value) => Number.isInteger(value) && value >= 1 && value <= 13);
    const pageSize = Math.max(1, Number(root.dataset.pageSize) || 12);
    const navigationShardCount = Math.min(2, shards.length);
    const search = root.querySelector('[data-pieces-search]');
    const grid = root.querySelector('[data-pieces-grid]');
    const status = root.querySelector('[data-pieces-status]');
    const indexProgress = root.querySelector('[data-pieces-index-progress]');
    const empty = root.querySelector('[data-pieces-empty]');
    const retryButton = root.querySelector('[data-pieces-retry]');
    const pagination = root.querySelector('[data-pieces-pagination]');
    const previous = root.querySelector('[data-pieces-previous]');
    const next = root.querySelector('[data-pieces-next]');
    const pageLabel = root.querySelector('[data-pieces-page-label]');

    const state = {
      index: [],
      filtered: [],
      indexReady: false,
      indexLoading: false,
      nextIndexShard: 0,
      legacyPageParam: null,
      legacyTotalPages: null,
      firstPageStarted: false,
      earlyIndexPrefix: null,
      refreshFirstPage: false,
      renderedPage: null,
      paginationVisible: false,
      generation: 0,
      currentPage: 1,
      pageStarts: [0],
      pages: new Map(),
      pageParams: new Map(),
      pendingPageParams: new Map(),
      indexPageParams: new Map(),
      fastCardFallbackShards: new Set(),
      cardCache: new Map(),
      pendingCards: new Map(),
      fastPreview: fastFirstCards ? {
        shard: shards[0],
        results: new Map(),
        pending: new Map(),
        rendered: [],
        nextToRender: 0,
        nextToRequest: 1,
        inFlight: 0,
        visibleCount: 0,
        unavailableCount: 0,
        skippedCount: 0,
        failed: false,
        hydrated: false
      } : null,
      busy: false,
      retryAction: null
    };

    const showError = (error, retryAction) => {
      // Never render HTTP/Liquid diagnostics in the production storefront,
      // including when ?pieces_debug=1. Keep recovery available.
      status.hidden = true;
      status.textContent = '';
      retryButton.hidden = grid.children.length > 0;
      state.retryAction = retryAction;
      grid.setAttribute('aria-busy', 'false');
      if (PIECES_DEBUG) {
        console.warn('[pieces:page:failed]', { error: error.message });
      }
    };

    const readLiteIndexShard = async (shard) => {
      // Lite index is available for shards 01–13.
      if (!Number.isInteger(shard) || shard < 1 || shard > 13) {
        return null;
      }

      const sectionId =
        `product-pieces-index-lite-${String(shard).padStart(2, '0')}`;

      const expectedShardKey = shard === 1
        ? 'liste_de_pieces_v2'
        : `liste_de_pieces_v2_${shard}`;

      const doc = await requestSection(
        sectionId,
        null,
        null,
        1
      );

      const group = doc.querySelector('[data-pieces-lite-index]');

      if (
        !group ||
        group.dataset.product !== productHandle ||
        Number(group.dataset.shard) !== shard ||
        group.dataset.shardKey !== expectedShardKey
      ) {
        throw new Error(`Lite index shard ${shard}: invalid response`);
      }

      // Larger shards are processed by the original paginated index.
      if (group.dataset.status !== 'ok') {
        if (group.dataset.status !== 'requires-pagination') {
          console.warn(
            `[product-pieces-async] Lite shard ${shard}: ` +
            `${group.dataset.status || 'unknown status'}`
          );
        }
        return null;
      }

      const totalItems = Number(group.dataset.count);

      if (
        !Number.isInteger(totalItems) ||
        totalItems < 1 ||
        totalItems > 50
      ) {
        throw new Error(`Lite index shard ${shard}: invalid count`);
      }

      const entries = [
        ...group.querySelectorAll('[data-piece-lite-item]')
      ].map((element) => ({
        shard,
        position: Number(element.dataset.position),
        metaobjectHandle: element.dataset.handle || '',
        title: element.dataset.title || '',
        productGid: element.dataset.productReference || ''
      }));

      if (
        entries.length !== totalItems ||
        entries.some((entry, index) =>
          !Number.isInteger(entry.position) ||
          entry.position !== index ||
          !entry.metaobjectHandle ||
          !entry.title ||
          !entry.productGid
        )
      ) {
        throw new Error(`Lite index shard ${shard}: incomplete references`);
      }

      return {
        entries,
        pageParam: null
      };
    };

    // A single V2 metafield may contain >50 references (e.g. 94 for Can-Am).
    // Read all Liquid pagination pages of that field before advancing shards.
    const readIndexShard = async (shard, onFirstPage = null) => {
      if (useHybridIndex && shard >= 1 && shard <= 13) {
        try {
          const liteResult = await readLiteIndexShard(shard);

          if (liteResult) {
            return liteResult;
          }
        } catch (error) {
          console.warn(
            '[product-pieces-async] Lite index fallback:',
            error.message
          );
        }
      }

      // Original paginated implementation remains unchanged.
      const sectionId = INDEX_SECTIONS[shard];
      const shardName = shard === 1
        ? 'liste_de_pieces_v2'
        : `liste_de_pieces_v2_${shard}`;
      const entries = [];
      let totalPages = 1;
      let totalItems = null;
      let pageParam = null;

      for (let page = 1; page <= totalPages; page++) {
        const doc = await requestSection(sectionId, pageParam, page);
        const group = doc.querySelector('[data-pieces-index-group]');
        const shardElement = group?.querySelector('[data-pieces-index-shard]');
        const reportedPage = Number(shardElement?.dataset.page);
        const reportedPages = Number(shardElement?.dataset.pages);
        const reportedItems = Number(shardElement?.dataset.items);
        const reportedParam = shardElement?.dataset.pageParam;

        if (
          !group ||
          group.dataset.product !== productHandle ||
          Number(group.dataset.startShard) !== shard ||
          Number(group.dataset.endShard) !== shard ||
          shardElement?.dataset.shard !== shardName ||
          reportedPage !== page ||
          !Number.isInteger(reportedPages) ||
          reportedPages < 1 || reportedPages > 50 ||
          !Number.isInteger(reportedItems) ||
          reportedItems < 1 ||
          reportedPages !== Math.ceil(reportedItems / 50) ||
          !reportedParam ||
          (page > 1 && (
            reportedPages !== totalPages ||
            reportedItems !== totalItems ||
            reportedParam !== pageParam
          ))
        ) {
          throw new Error(`Index shard ${shard}, page ${page}: invalid response`);
        }

        if (page === 1) {
          totalPages = reportedPages;
          totalItems = reportedItems;
          pageParam = reportedParam;
        }

        const pageEntries = [
          ...shardElement.querySelectorAll('[data-piece-index-item]')
        ].map((element) => ({
          shard,
          position: Number(element.dataset.shardPosition),
          metaobjectHandle: element.dataset.handle || '',
          title: element.dataset.adminName || element.dataset.visibleName || '',
          productGid: element.dataset.productReference || ''
        }));

        const expectedCount = Math.min(50, totalItems - entries.length);
        if (
          pageEntries.length !== expectedCount ||
          pageEntries.some((entry, index) =>
            !Number.isInteger(entry.position) ||
            entry.position !== entries.length + index ||
            !entry.metaobjectHandle || !entry.productGid || !entry.title
          )
        ) {
          throw new Error(`Index shard ${shard}, page ${page}: incomplete references`);
        }
        entries.push(...pageEntries);

        // The first validated pagination page can start card loading.
        // Continue reading subsequent pages in the background.
        if (
          page === 1 &&
          totalPages > 1 &&
          typeof onFirstPage === 'function'
        ) {
          onFirstPage(pageEntries);
        }
      }

      if (entries.length !== totalItems) {
        throw new Error(`Index shard ${shard}: expected ${totalItems}, got ${entries.length}`);
      }
      return { entries, pageParam };
    };

    // Legacy list.metaobject_reference / list.product_reference may contain
    // more than 50 references: paginate its lightweight index by 50.
    const readLegacyIndexPage = async (pageNumber, pageParam) => {
      const doc = await requestSection(
        'product-pieces-index-legacy', pageParam, pageNumber
      );
      const group = doc.querySelector('[data-pieces-legacy-index]');
      const currentPage = Number(group?.dataset.page);
      const pages = Number(group?.dataset.pages);
      const resolvedParam = group?.dataset.pageParam;
      if (
        !group || group.dataset.product !== productHandle ||
        currentPage !== pageNumber || !Number.isInteger(pages) ||
        pages < 1 || pages > 50 || !resolvedParam ||
        (pageParam && resolvedParam !== pageParam)
      ) {
        throw new Error(`Legacy index page ${pageNumber}: invalid response`);
      }

      const entries = [...group.querySelectorAll('[data-piece-legacy-index-item]')]
        .map((element) => ({
          shard: 0,
          position: Number(element.dataset.position),
          metaobjectHandle: element.dataset.handle || '',
          title: element.dataset.title || '',
          productGid: element.dataset.productReference || ''
        }));

      if (
        entries.length === 0 || entries.length > 50 ||
        entries.some((entry) =>
          !Number.isInteger(entry.position) || entry.position < 0 ||
          !entry.metaobjectHandle || !entry.title || !entry.productGid
        )
      ) {
        throw new Error(`Legacy index page ${pageNumber}: incomplete references`);
      }
      return { entries, pages, pageParam: resolvedParam };
    };

    const parseCardResponse = (doc, item) => {
      const rootResponse = doc.querySelector('[data-piece-card-response]');
      const entry = rootResponse?.querySelector('[data-piece-card-entry]');
      const card = entry?.querySelector('[data-piece-card]');

      if (
        rootResponse?.dataset.status !== 'ok' ||
        rootResponse.dataset.product !== productHandle ||
        Number(rootResponse.dataset.shard) !== item.shard ||
        Number(entry?.dataset.position) !== item.position ||
        entry?.dataset.metaobjectHandle !== item.metaobjectHandle ||
        entry?.dataset.productGid !== item.productGid
      ) {
        throw new Error(
          `Card mismatch: shard ${item.shard}, position ${item.position}`
        );
      }

      const cardStatus = entry.dataset.status;
      if (
        (cardStatus !== 'available' && cardStatus !== 'unavailable') ||
        (cardStatus === 'available' && !card) ||
        (cardStatus === 'unavailable' && card)
      ) {
        throw new Error(`Card status invalid: ${item.metaobjectHandle}`);
      }

      return {
        status: cardStatus,
        element: cardStatus === 'available' ? card.cloneNode(true) : null
      };
    };

    // Concurrent cards in one shard reuse a SINGLE pagination bootstrap request.
    const getCardPagination = async (shard, sectionId) => {
      if (state.pageParams.has(shard)) return state.pageParams.get(shard);

      let pending = state.pendingPageParams.get(shard);
      if (!pending) {
        pending = (async () => {
          const firstDoc = await requestSection(sectionId);
          const response = firstDoc.querySelector('[data-piece-card-response]');
          const pageParam = response?.dataset.pageParam;

          if (
            response?.dataset.status !== 'ok' ||
            response.dataset.product !== productHandle ||
            Number(response.dataset.shard) !== shard ||
            !pageParam
          ) {
            throw new Error(`Missing card pagination for shard ${shard}`);
          }

          return { pageParam, firstDoc };
        })();
        state.pendingPageParams.set(shard, pending);
      }

      try {
        const context = await pending;
        state.pageParams.set(shard, context);
        return context;
      } finally {
        if (state.pendingPageParams.get(shard) === pending) {
          state.pendingPageParams.delete(shard);
        }
      }
    };

    // Show an ordered prefix of up to pageSize AVAILABLE cards, skipping Drafts.
    // The index remains authoritative for identities and page navigation.
    const renderFastPreview = () => {
      const preview = state.fastPreview;
      if (!preview || state.firstPageStarted) return;

      while (
        preview.visibleCount < pageSize &&
        preview.results.has(preview.nextToRender)
      ) {
        const result = preview.results.get(preview.nextToRender);
        preview.rendered.push(result);
        preview.nextToRender++;
        if (result.status === 'available') {
          preview.visibleCount++;
          appendCardBatch([result.element]);
          // Visible cards take priority over the background Index status.
          indexProgress.hidden = true;
          status.hidden = true;
          status.textContent = '';
          retryButton.hidden = true;
          if (PIECES_DEBUG) {
            console.info('[pieces:fast-preview:visible]', {
              shard: result.shard,
              position: result.position,
              elapsedMs: Math.round(performance.now() - fastPreviewStartedAt)
            });
          }
        }
      }
    };

    const parseFastPreviewCard = (doc, shard, position) => {
      const response = doc.querySelector('[data-piece-card-response]');
      const entry = response?.querySelector('[data-piece-card-entry]');
      const card = entry?.querySelector('[data-piece-card]');
      const cardStatus = entry?.dataset.status;
      const metaobjectHandle = entry?.dataset.metaobjectHandle;
      const productGid = entry?.dataset.productGid;

      if (
        response?.dataset.status !== 'ok' ||
        response.dataset.product !== productHandle ||
        Number(response.dataset.shard) !== shard ||
        Number(response.dataset.page) !== position + 1 ||
        Number(entry?.dataset.position) !== position ||
        !metaobjectHandle || !productGid ||
        (cardStatus !== 'available' && cardStatus !== 'unavailable') ||
        (cardStatus === 'available' && !card) ||
        (cardStatus === 'unavailable' && card)
      ) {
        throw new Error(`Fast preview card invalid: shard ${shard}, position ${position}`);
      }

      return {
        shard, position, metaobjectHandle, productGid,
        status: cardStatus,
        element: cardStatus === 'available' ? card.cloneNode(true) : null
      };
    };

    const fastPreviewStartedAt = performance.now();

    const startFastFirstCards = () => {
      const preview = state.fastPreview;
      if (!preview || preview.shard == null) return;

      const shard = preview.shard;
      const sectionId = shard === 0
        ? 'product-pieces-card-legacy'
        : `product-pieces-card-${String(shard).padStart(2, '0')}`;
      const maxPositions = Math.min(
        FAST_PREVIEW_MAX_POSITIONS,
        Math.max(pageSize * 2, pageSize + CARD_BATCH_SIZE)
      );

      const accept = (position, doc) => {
        const result = parseFastPreviewCard(doc, shard, position);
        preview.results.set(position, result);
        if (result.status === 'unavailable') preview.unavailableCount++;
        renderFastPreview();
        return result;
      };

      // A sliding window limits speculative requests to four at a time,
      // without scanning far beyond a slow earlier position.
      const fillPreviewQueue = (pageParam) => {
        if (state.firstPageStarted || preview.failed ||
            preview.visibleCount >= pageSize) return;

        const maxWindow = Math.min(
          maxPositions,
          pageSize + preview.unavailableCount + preview.skippedCount,
          preview.nextToRender + CARD_BATCH_SIZE
        );
        while (
          preview.inFlight < CARD_BATCH_SIZE &&
          preview.nextToRequest < maxWindow
        ) {
          const position = preview.nextToRequest++;
          const key = `${shard}:${position}`;
          preview.inFlight++;

          // Do not run an independent retry loop here: loadCard() owns retries
          // after the Index is available, and can reuse this pending request.
          const pending = requestSection(
            sectionId, pageParam, position + 1, 1
          ).then((doc) => accept(position, doc));
          preview.pending.set(key, pending);

          void pending.catch((error) => {
            // A failed card must not block the remaining preview positions.
            // This is a temporary omission, not a confirmed Draft/unavailable.
            preview.results.set(position, {
              shard, position, status: 'failed'
            });
            preview.skippedCount++;
            renderFastPreview();
            if (PIECES_DEBUG) {
              console.warn('[pieces:fast-preview:skip]', {
                shard, position, error: error.message
              });
            }
          }).finally(() => {
            preview.inFlight--;
            if (preview.pending.get(key) === pending) {
              preview.pending.delete(key);
            }
            fillPreviewQueue(pageParam);
          });
        }
      };

      const firstKey = `${shard}:0`;
      const first = getCardPagination(shard, sectionId).then((context) => {
        const firstResult = accept(0, context.firstDoc);
        fillPreviewQueue(context.pageParam);
        return firstResult;
      });
      preview.pending.set(firstKey, first);
      void first.catch((error) => {
        preview.failed = true;
        // Preview errors must not block the Index-based loader.
        if (PIECES_DEBUG) {
          console.info('[pieces:fast-preview:skip]', {
            position: 0, error: error.message
          });
        }
      }).finally(() => {
        if (preview.pending.get(firstKey) === first) {
          preview.pending.delete(firstKey);
        }
      });
    };

    const loadCard = async (item) => {
      const key = `${item.shard}:${item.position}`;
      if (state.cardCache.has(key)) return state.cardCache.get(key);
      if (state.pendingCards.has(key)) return state.pendingCards.get(key);

      const promise = (async () => {
        const sectionId = item.shard === 0
          ? 'product-pieces-card-legacy'
          : `product-pieces-card-${String(item.shard).padStart(2, '0')}`;

        // Reuse speculative cards only after validating their identities
        // against the authoritative Index entry.
        const preview = state.fastPreview;
        if (preview?.shard === item.shard) {
          const pending = preview.pending.get(key);
          if (pending) {
            try { await pending; } catch (_) { /* Normal loader will retry. */ }
          }
          const prefetched = preview.results.get(item.position);
          if (
            prefetched &&
            prefetched.metaobjectHandle === item.metaobjectHandle &&
            prefetched.productGid === item.productGid
          ) {
            const result = {
              status: prefetched.status,
              element: prefetched.element?.cloneNode(true) || null
            };
            state.cardCache.set(key, result);
            return result;
          }
        }

        // Reuse the index pagination parameter to load cards directly.
        // Validate the response before caching or rendering anything.
        const fastPageParam = optimizedCardLoading &&
          !state.fastCardFallbackShards.has(item.shard)
          ? state.indexPageParams.get(item.shard)
          : null;

        if (fastPageParam) {
          try {
            const doc = await requestSection(
              sectionId,
              fastPageParam,
              item.position + 1,
              3
            );

            const result = parseCardResponse(doc, item);
            state.cardCache.set(key, result);
            return result;
          } catch (error) {
            // Retrying through the pagination bootstrap does not fix
            // Liquid rendering failures and adds unnecessary requests.
            if (!error.message.startsWith('Card mismatch:')) {
              throw error;
            }

            // Shopify may use a different pagination parameter.
            // Fall back to the original bootstrap mechanism.
            state.fastCardFallbackShards.add(item.shard);

            console.warn(
              `[product-pieces-async] Fast card loading fallback: ` +
              `shard=${item.shard}, ` +
              `position=${item.position}, ` +
              `reason=${error.message}`
            );
          }
        }

        let context = await getCardPagination(item.shard, sectionId);

        // Shopify can occasionally return a successful section response for
        // the wrong paginate position. Validate it and retry that position;
        // never silently render a mismatched product card.
        for (let attempt = 1; attempt <= 3; attempt++) {
          const doc = (item.position === 0 && attempt === 1)
            ? context.firstDoc
            : await requestSection(sectionId, context.pageParam, item.position + 1);
          try {
            const result = parseCardResponse(doc, item);
            state.cardCache.set(key, result);
            return result;
          } catch (error) {
            if (!error.message.startsWith('Card mismatch:') || attempt === 3) {
              throw error;
            }
            const response = doc.querySelector('[data-piece-card-response]');
            const entry = response?.querySelector('[data-piece-card-entry]');
            console.warn('[product-pieces-async] Retrying mismatched card', {
              attempt,
              expected: { shard: item.shard, position: item.position, handle: item.metaobjectHandle },
              received: {
                shard: response?.dataset.shard,
                page: response?.dataset.page,
                position: entry?.dataset.position,
                handle: entry?.dataset.metaobjectHandle
              }
            });
            await sleep(attempt * 500);
            if (attempt === 2) {
              state.pageParams.delete(item.shard);
              context = await getCardPagination(item.shard, sectionId);
            }
          }
        }
        throw new Error(`Card mismatch: shard ${item.shard}, position ${item.position}`);
      })();

      state.pendingCards.set(key, promise);
      try {
        return await promise;
      } finally {
        state.pendingCards.delete(key);
      }
    };

    const updatePagination = () => {
      const navigationReady = state.nextIndexShard >= navigationShardCount;
      const current = state.pages.get(state.currentPage);
      const pageReady = !state.busy &&
        state.renderedPage === state.currentPage &&
        current?.cards.length > 0;
      const hasNavigation = state.currentPage > 1 || current?.hasNext;

      // Once shown, navigation stays visible across page transitions.
      // Only the buttons are disabled while the next page is loading.
      if (navigationReady && pageReady && hasNavigation) {
        state.paginationVisible = true;
      }
      pagination.hidden = !state.paginationVisible;
      pageLabel.textContent = `Page ${state.currentPage}`;
      previous.disabled = state.busy || state.currentPage <= 1;
      next.disabled = !pageReady || !current?.hasNext;
    };

    let firstCardLogged = false;
    const appendCardBatch = (cards) => {
      const fragment = document.createDocumentFragment();
      for (const card of cards) fragment.appendChild(card.cloneNode(true));
      grid.appendChild(fragment);
      if (PIECES_DEBUG && !firstCardLogged && cards.length) {
        firstCardLogged = true;
        console.info('[pieces:first-card:dom]', {
          mode: state.firstPageStarted ? 'index' : 'preview',
          durationMs: Math.round(performance.now() - fastPreviewStartedAt)
        });
      }
    };

    const displayPage = (pageData, page, alreadyRendered = false) => {
      // Cached pages are rendered at once. Newly fetched pages were already
      // appended in groups of four while their remaining cards loaded.
      if (!alreadyRendered) {
        const fragment = document.createDocumentFragment();
        for (const card of pageData.cards) fragment.appendChild(card.cloneNode(true));
        grid.replaceChildren(fragment);
      }

      grid.setAttribute('aria-busy', 'false');
      // A failed request is not the same as a confirmed empty result.
      empty.hidden = pageData.cards.length > 0 || !state.indexReady ||
        Boolean(pageData.hadCardErrors);
      status.textContent = '';
      status.hidden = true;
      state.currentPage = page;
      state.renderedPage = page;
      updatePagination();
    };

    // A retried incomplete page can move the start of every later page.
    // Invalidate all downstream cards/cursors before recalculating it.
    const invalidatePagesFrom = (page) => {
      for (const cachedPage of state.pages.keys()) {
        if (cachedPage >= page) state.pages.delete(cachedPage);
      }
      state.pageStarts.length = page;
    };

    const loadPage = async (page, generation) => {
      if (state.busy) return;
      if (state.pageStarts[page - 1] == null) return;
      state.currentPage = page;
      state.renderedPage = null;
      state.busy = true;
      retryButton.hidden = true;
      state.retryAction = null;
      grid.setAttribute('aria-busy', 'true');
      updatePagination();

      try {
        if (state.pages.has(page)) {
          const cached = state.pages.get(page);
          if (!fastFirstCards || !cached.hadCardErrors) {
            if (generation === state.generation) displayPage(cached, page);
            return;
          }
          // Retry temporary failures, and discard downstream page boundaries:
          // a recovered card can shift the start of every subsequent page.
          invalidatePagesFrom(page);
        }

        // Keep the already visible fast preview only when the Index confirms
        // every previewed position and reference identity.
        const preview = state.fastPreview;
        const preservePreview = page === 1 &&
          !preview?.hydrated &&
          preview?.rendered.length > 0 &&
          preview.rendered.every((item, index) => {
            // A skipped HTTP error has no validated identity. A later retry
            // might recover that card, so positional preview reuse is unsafe.
            if (item.status === 'failed') return false;
            const indexed = state.filtered[index];
            return indexed && indexed.shard === item.shard &&
              indexed.position === item.position &&
              indexed.metaobjectHandle === item.metaobjectHandle &&
              indexed.productGid === item.productGid;
          });
        const preservedCount = preservePreview
          ? preview.rendered.filter((item) => item.status === 'available').length
          : 0;
        if (preview && page === 1) preview.hydrated = true;
        // Keep a visible preview until the first validated Index card is
        // ready, instead of clearing it while waiting on slow card requests.
        let replacePreviewOnFirstCard = !preservePreview &&
          page === 1 && grid.children.length > 0;
        if (!preservePreview && !replacePreviewOnFirstCard) {
          grid.replaceChildren();
        }
        const appendLoadedCards = (cards) => {
          if (replacePreviewOnFirstCard) {
            grid.replaceChildren();
            replacePreviewOnFirstCard = false;
          }
          appendCardBatch(cards);
        };
        empty.hidden = true;
        status.textContent = '';
        status.hidden = true;

        const cards = [];
        let hadCardErrors = false;
        let renderedCount = 0;
        let cursor = state.pageStarts[page - 1];
        let nextCursor = null;

        // Fetch a few card responses concurrently, preserving reference order.
        // Continue past Drafts, and resolve one extra available card for Next.
        while (cursor < state.filtered.length) {
          const batch = state.filtered.slice(cursor, cursor + CARD_BATCH_SIZE);

          if (progressiveCardRendering) {
            // Start all requests concurrently, but consume them in reference
            // order so slow requests cannot reorder the product cards.
            // Handle rejection immediately to avoid unhandled promises
            // when an extra lookahead request is no longer needed.
            const pendingResults = batch.map((item) =>
              loadCard(item).then(
                (result) => ({ result, error: null }),
                (error) => ({ result: null, error })
              )
            );

            for (const pending of pendingResults) {
              const { result, error } = await pending;

              // Ignore results belonging to an outdated search.
              if (generation !== state.generation) return;
              if (error) {
                // Keep the original behavior unless Fast Preview is enabled.
                // Do not cache the failure as unavailable: it may be transient.
                if (!fastFirstCards) throw error;
                hadCardErrors = true;
                if (PIECES_DEBUG) {
                  console.warn('[pieces:card:skip]', {
                    page,
                    shard: state.filtered[cursor]?.shard,
                    position: state.filtered[cursor]?.position,
                    error: error.message
                  });
                }
                cursor++;
                continue;
              }

              if (result.status === 'available') {
                // Keep the first extra available item for the next page.
                if (cards.length === pageSize) {
                  nextCursor = cursor;
                  break;
                }

                cards.push(result.element);

                // Skip cards already visible in the validated fast preview.
                if (renderedCount >= preservedCount) {
                  appendLoadedCards([result.element]);
                }
                renderedCount++;
              }

              // Draft/unavailable references still advance the cursor.
              cursor++;
            }
          } else {
            // Original behavior: wait for a full batch.
            const results = await Promise.all(batch.map(loadCard));
            if (generation !== state.generation) return;

            for (let i = 0; i < batch.length; i++) {
              if (results[i].status === 'available') {
                if (cards.length === pageSize) {
                  nextCursor = cursor;
                  break;
                }
                cards.push(results[i].element);
              }
              cursor++;
            }

            while (cards.length - renderedCount >= CARD_BATCH_SIZE) {
              if (generation !== state.generation) return;
              appendLoadedCards(
                cards.slice(renderedCount, renderedCount + CARD_BATCH_SIZE)
              );
              renderedCount += CARD_BATCH_SIZE;
            }
          }

          if (nextCursor !== null) break;
        }

        // A page may end with fewer than four cards (or fewer than four
        // available products); show that last group on completion.
        if (generation !== state.generation) return;
        if (renderedCount < cards.length) {
          appendLoadedCards(cards.slice(renderedCount));
        }
        // No Index cards were found; discard any unverified preview before
        // displaying an empty result or a neutral retry control.
        if (replacePreviewOnFirstCard) grid.replaceChildren();

        const pageData = {
          cards,
          hasNext: nextCursor !== null,
          scannedUntil: cursor,
          hadCardErrors
        };
        state.pageStarts[page] = nextCursor;
        state.pages.set(page, pageData);
        displayPage(pageData, page, true);
        if (fastFirstCards && hadCardErrors && cards.length === 0) {
          retryButton.hidden = false;
          state.retryAction = () => {
            state.pages.delete(page);
            void loadPage(page, state.generation);
          };
        }
      } catch (error) {
        if (generation === state.generation) {
          showError(error, () => loadPage(page, generation));
        }
      } finally {
        if (generation === state.generation) {
          state.busy = false;
          grid.setAttribute('aria-busy', 'false');
          if (state.refreshFirstPage && state.indexReady) {
            state.refreshFirstPage = false;
            const firstPage = state.pages.get(1);
            if (firstPage && !firstPage.hasNext &&
                firstPage.scannedUntil < state.filtered.length) {
              invalidatePagesFrom(1);
              void loadPage(1, generation);
            }
          }
          updatePagination();
        }
      }
    };

    const applySearch = () => {
      if (!state.indexReady) return;
      const query = normalize(search?.value);
      retryButton.hidden = true;
      state.retryAction = null;
      status.textContent = '';
      status.hidden = true;

      state.generation++;
      // A previous page request may still be in flight; its generation guard
      // prevents it from rendering into this search.
      state.busy = false;
      state.filtered = state.index.filter((item) =>
        normalize(item.title).includes(query)
      );
      state.pageStarts = [0];
      state.pages.clear();
      state.currentPage = 1;
      state.renderedPage = null;
      state.paginationVisible = false;
      grid.replaceChildren();
      pagination.hidden = true;

      if (!state.filtered.length) {
        status.textContent = '';
        status.hidden = true;
        grid.setAttribute('aria-busy', 'false');
        empty.hidden = false;
      } else {
        loadPage(1, state.generation);
      }
    };

    const loadIndex = async () => {
      if (state.indexLoading || state.indexReady) return;
      state.indexLoading = true;
      retryButton.hidden = true;
      state.retryAction = null;
      indexProgress.hidden = state.nextIndexShard >= navigationShardCount;
      indexProgress.textContent = 'Chargement...';
      status.hidden = true;

      if (!state.firstPageStarted) {
        if (search) search.disabled = true;
        grid.setAttribute('aria-busy', 'true');
        empty.hidden = true;
      }

      try {
        let legacyPage = state.nextIndexShard + 1;
        let legacyPages = state.legacyTotalPages ?? Infinity;
        let legacyParam = state.legacyPageParam;
        while (legacyMode
          ? legacyPage <= legacyPages
          : state.nextIndexShard < shards.length) {
          let entries;
          if (legacyMode) {
            const response = await readLegacyIndexPage(legacyPage, legacyParam);
            entries = response.entries;
            legacyPages = response.pages;
            legacyParam = response.pageParam;
            state.legacyTotalPages = legacyPages;
            state.legacyPageParam = legacyParam;
            legacyPage++;
          } else {
            const shard = shards[state.nextIndexShard];

            const onFirstPage =
              earlyIndexRendering &&
              state.nextIndexShard === 0
                ? (pageEntries) => {
                    if (state.firstPageStarted) return;

                    // Publish only the validated first page.
                    state.index.push(...pageEntries);
                    state.filtered.push(...pageEntries);

                    // Remember this prefix to avoid duplicate entries.
                    state.earlyIndexPrefix = {
                      shard,
                      entries: pageEntries
                    };

                    state.firstPageStarted = true;
                    state.generation++;

                    void loadPage(1, state.generation);
                  }
                : null;

            const result = await readIndexShard(shard, onFirstPage);
            entries = result.entries;

            // The first pagination page was already added to the index.
            // Validate it and append only the remaining references.
            if (state.earlyIndexPrefix?.shard === shard) {
              const prefix = state.earlyIndexPrefix.entries;

              const prefixMatches = prefix.every((entry, index) => {
                const actual = entries[index];
                return (
                  actual &&
                  actual.position === entry.position &&
                  actual.metaobjectHandle === entry.metaobjectHandle &&
                  actual.productGid === entry.productGid &&
                  actual.title === entry.title
                );
              });

              if (!prefixMatches) {
                throw new Error(
                  `Early index shard ${shard}: references changed`
                );
              }

              entries = entries.slice(prefix.length);
              state.earlyIndexPrefix = null;
            }
            if (optimizedCardLoading) {
              state.indexPageParams.set(shard, result.pageParam);
            }
          }
          state.index.push(...entries);
          state.filtered.push(...entries);
          state.nextIndexShard++;

          if (state.nextIndexShard >= navigationShardCount) {
            indexProgress.hidden = true;
            updatePagination();
          }

          // Prioritize rendering the first page before loading more shards.
          // The timeout prevents background indexing from being blocked
          // indefinitely by slow or unavailable product references.
          if (!state.firstPageStarted) {
            state.firstPageStarted = true;
            state.generation++;

            const firstPagePromise = loadPage(1, state.generation);

            if (prioritizeFirstPage) {
              await Promise.race([
                firstPagePromise,
                sleep(FIRST_PAGE_PRIORITY_TIMEOUT)
              ]);
            }
          }
        }

        state.indexReady = true;
        indexProgress.hidden = true;
        if (search) search.disabled = false;

        // Reconcile the empty state after background indexing finishes.
        const currentPageData = state.pages.get(state.currentPage);
        if (currentPageData && !state.busy) {
          empty.hidden = currentPageData.cards.length > 0;
        }

        updatePagination();

        // If a small first shard produced too few cards before the rest of
        // the index arrived, fill the same page from subsequent shards.
        const firstPage = state.pages.get(1);
        if (firstPage && !firstPage.hasNext &&
            firstPage.scannedUntil < state.filtered.length) {
          if (state.busy) {
            state.refreshFirstPage = true;
          } else {
            invalidatePagesFrom(1);
            void loadPage(1, state.generation);
          }
        }
      } catch (error) {
        // The Index can fail independently of cards. Never expose raw errors
        // in the storefront (even with ?pieces_debug=1).
        indexProgress.hidden = true;
        indexProgress.textContent = '';
        status.hidden = true;
        status.textContent = '';
        retryButton.hidden = grid.children.length > 0;
        if (!state.firstPageStarted) {
          grid.setAttribute('aria-busy', 'false');
        }
        // Manual recovery remains available in debug mode.
        state.retryAction = loadIndex;
        if (PIECES_DEBUG) {
          console.warn('[pieces:index:failed]', { error: error.message });
        }
      } finally {
        state.indexLoading = false;
      }
    };

    let debounceTimer;
    search?.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applySearch, 250);
    });

    previous?.addEventListener('click', () => {
      if (!state.busy && state.currentPage > 1) {
        loadPage(state.currentPage - 1, state.generation);
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    next?.addEventListener('click', () => {
      const current = state.pages.get(state.currentPage);
      if (!state.busy && current?.hasNext) {
        loadPage(state.currentPage + 1, state.generation);
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    retryButton?.addEventListener('click', () => {
      if (!state.busy) state.retryAction?.();
    });

    if (shards.length) {
      void loadIndex();

      if (fastFirstCards) startFastFirstCards();

      // Start the first card pagination request while the index loads.
      // getCardPagination shares the pending request with loadCard().
      if (!fastFirstCards && prefetchCardPagination && !optimizedCardLoading) {
        const firstShard = shards[0];
        const sectionId =
          `product-pieces-card-${String(firstShard).padStart(2, '0')}`;

        void getCardPagination(firstShard, sectionId).catch((error) => {
          // The normal card loader can retry later.
          console.warn(
            '[product-pieces-async] Card pagination prefetch failed:',
            error.message
          );
        });
      }
    } else {
      status.hidden = false;
      status.textContent = 'Aucune vue éclatée';
      grid.setAttribute('aria-busy', 'false');
    }
  };

  const initializeAll = (container = document) => {
    if (container.matches?.('[data-pieces-async]')) initialize(container);
    container.querySelectorAll('[data-pieces-async]').forEach(initialize);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
